/**
 * CelebrateSubmit.parts — Self-contained sub-components extracted from
 * CelebrateSubmit.jsx so the top-level file stays focused on the
 * multi-step form wiring. Everything exported here is either purely
 * presentational or a self-contained widget with its own local state.
 *
 * Nothing here reads from CelebrateSubmit's parent state directly — all
 * data flows in through props and results flow back through the `onApply`
 * / `onClose` callbacks.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronRight,
  Pencil,
  Sparkles,
  Loader2,
  Check,
  TreePine,
  ExternalLink,
  Plus,
} from "lucide-react";

import { http } from "@/lib/api";
import SmartTextInput from "@/components/SmartTextInput";
import SectionPrivacy from "@/components/SectionPrivacy";
import { formatServiceOrderText, buildShareLinks } from "@/lib/serviceOrderShare";

const border = { borderColor: "var(--wofy-border)" };

/** Collapsible accordion used inside Step 6 (Optional Details). */
export function OptionalAccordion({ title, testid, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border overflow-hidden" style={border} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium hover:bg-black/5"
      >
        <span>{title}</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="p-4 border-t space-y-3" style={border}>{children}</div>}
    </div>
  );
}

/** Review card shown on Step 7 — one per past step. */
export function ReviewCard({ label, rows, goTo, preview }) {
  const hasContent = rows.some(([, v]) => v && String(v).trim() && String(v) !== "0" && String(v) !== "0 entries" && String(v) !== "0 link(s)");
  return (
    <div className="wofy-card flex items-start gap-3" data-testid={`review-card-${label.toLowerCase()}`}>
      {preview && <img src={preview} alt="" className="w-14 h-14 object-cover rounded-lg" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="overline">{label}</div>
          <button type="button" onClick={goTo} className="text-xs inline-flex items-center gap-1 underline opacity-70 hover:opacity-100" data-testid={`review-edit-${label.toLowerCase()}`}>
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
        {hasContent ? (
          <dl className="mt-1 text-sm space-y-0.5">
            {rows.filter(([, v]) => v && String(v).trim() && String(v) !== "0" && String(v) !== "0 entries" && String(v) !== "0 link(s)").map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-2">
                <dt className="text-[10px] font-mono uppercase tracking-wider opacity-60 shrink-0">{k}</dt>
                <dd className="min-w-0 break-words">{String(v)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="mt-1 text-xs italic opacity-60">Nothing added yet — tap Edit to fill it in.</p>
        )}
      </div>
    </div>
  );
}

export function SectionCard({ title, note, children, privacyValue, onPrivacyChange, privacyTestid, privacyLocked = false }) {
  return (
    <div className="wofy-card space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider">{title}</div>
          {note && <div className="text-[11px] mt-1" style={{ color: "var(--wofy-text-soft)" }}>{note}</div>}
        </div>
        {privacyLocked ? (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)", opacity: 0.6 }}>
            Always public
          </span>
        ) : (
          <SectionPrivacy value={privacyValue || "public"} onChange={onPrivacyChange} testidPrefix={privacyTestid} />
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
      <input required={required} type={type} placeholder={placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={border} data-testid={testid} />
    </label>
  );
}

export function TextArea({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
      <div className="mt-1">
        <SmartTextInput value={value || ""} onChange={onChange} rows={rows} testid={testid} />
      </div>
    </label>
  );
}

export function FamilyTreeCtaModal({ modal, onClose }) {
  const { submitter_tree, deceased_tree, status } = modal;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="mem-cta-modal">
      <div className="max-w-lg w-full rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <TreePine className="w-5 h-5" />
          <h3 className="font-display text-2xl">Preserve their legacy</h3>
        </div>
        <p className="text-sm text-black/70">
          {status === "approved"
            ? "Your memorial is live. Consider linking it to a family tree so future generations can find it."
            : "Thank you — the memorial is now under review. You can already start building a family tree to link it to."}
        </p>

        <div className="mt-4 space-y-2">
          {deceased_tree ? (
            <Link to={`/family-trees/${deceased_tree.slug}`} className="block wofy-btn-primary w-full text-center" data-testid="mem-cta-open-tree">
              <ExternalLink className="w-4 h-4" /> Visit {deceased_tree.name}
            </Link>
          ) : submitter_tree ? (
            <Link to={`/family-trees/${submitter_tree.slug}`} className="block wofy-btn-primary w-full text-center" data-testid="mem-cta-visit-my-tree">
              <ExternalLink className="w-4 h-4" /> Visit my family tree — {submitter_tree.name}
            </Link>
          ) : (
            <Link to="/family-trees/new" className="block wofy-btn-primary w-full text-center" data-testid="mem-cta-create-tree">
              <Plus className="w-4 h-4" /> Create Your Family Tree
            </Link>
          )}
          <button type="button" onClick={onClose} className="wofy-btn-ghost w-full" data-testid="mem-cta-close">
            {status === "approved" ? "See the memorial" : "Back to Celebrate a Life"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * AI Funeral Service Order Suggestor
 *
 * Collapsible mini-panel that lets the family provide religion /
 * cultural notes / favourite music etc, then hits
 * POST /api/memorials/service-order/suggest and pipes the structured
 * result into the parent's `funeralProgram.*` free-text fields.
 * ------------------------------------------------------------------ */
export function AiServiceOrderSuggestor({ form, onApply }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // last AI response — retained for share
  const [prefs, setPrefs] = useState({
    religion: "", culture: "", service_type: "burial",
    favourite_music: "", favourite_readings: "",
    special_requests: "", charity_for_donations: "",
  });

  const run = async () => {
    setBusy(true);
    try {
      const payload = {
        full_name: form?.full_name || undefined,
        preferred_name: form?.preferred_name || undefined,
        birth_date: form?.birth_date || undefined,
        death_date: form?.death_date || undefined,
        occupation: form?.profession || undefined,
        ...Object.fromEntries(Object.entries(prefs).filter(([, v]) => v && String(v).trim())),
      };
      const { data } = await http.post("/memorials/service-order/suggest", payload);
      if (!data?.order_of_service?.length) {
        toast.error("AI could not draft a service — please try again.");
        return;
      }
      onApply(data);
      setResult(data);
      toast.success(`Drafted ${data.order_of_service.length} ceremony steps. Edit freely below.`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI is temporarily unavailable — please try again in a minute.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="mb-3 rounded-xl border-2 border-dashed p-3"
      style={{ borderColor: "#B25D3844", background: "#FDFBF7" }}
      data-testid="ai-service-order-suggestor"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5" style={{ color: "#B25D38" }} />
          <div>
            <div className="text-sm font-medium">AI-suggested ceremony order</div>
            <div className="text-xs opacity-70 leading-snug">Get a personalised order of service, hymns, readings and speakers drafted for you — then edit freely.</div>
          </div>
        </div>
        <div className="flex gap-2">
          {!open && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-xs rounded-full px-3 py-1.5 font-medium"
              style={{ background: "#B25D38", color: "white" }}
              data-testid="ai-service-order-open"
            >
              ✨ {result ? "Re-draft with AI" : "Draft with AI"}
            </button>
          )}
        </div>
      </div>

      {result && !open && (
        <ServiceOrderShareRow result={result} personName={form?.full_name} />
      )}


      {open && (
        <div className="mt-3 space-y-2">
          <div className="grid md:grid-cols-2 gap-2">
            <label className="text-xs">
              <span className="opacity-70">Religion / tradition</span>
              <input
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                placeholder="e.g. Catholic, Hindu, Muslim, Secular, Interfaith"
                value={prefs.religion}
                onChange={(e) => setPrefs({ ...prefs, religion: e.target.value })}
                data-testid="ai-service-religion"
              />
            </label>
            <label className="text-xs">
              <span className="opacity-70">Service type</span>
              <select
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                value={prefs.service_type}
                onChange={(e) => setPrefs({ ...prefs, service_type: e.target.value })}
                data-testid="ai-service-type"
              >
                <option value="burial">Burial</option>
                <option value="cremation">Cremation</option>
                <option value="memorial">Memorial (no burial/cremation)</option>
              </select>
            </label>
            <label className="text-xs md:col-span-2">
              <span className="opacity-70">Cultural notes (optional)</span>
              <input
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                placeholder="e.g. Kerala Syrian Christian, North Indian Hindu, Sindhi, Punjabi Sikh…"
                value={prefs.culture}
                onChange={(e) => setPrefs({ ...prefs, culture: e.target.value })}
                data-testid="ai-service-culture"
              />
            </label>
            <label className="text-xs md:col-span-2">
              <span className="opacity-70">Favourite music / songs (optional)</span>
              <input
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                placeholder="e.g. Amazing Grace, How Great Thou Art, a favourite film song…"
                value={prefs.favourite_music}
                onChange={(e) => setPrefs({ ...prefs, favourite_music: e.target.value })}
                data-testid="ai-service-music"
              />
            </label>
            <label className="text-xs md:col-span-2">
              <span className="opacity-70">Special requests / family notes (optional)</span>
              <textarea
                rows={2}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                placeholder="Anything the family wants to include — a specific reading, a moment of silence, a prayer, honouring their profession, favourite hobbies…"
                value={prefs.special_requests}
                onChange={(e) => setPrefs({ ...prefs, special_requests: e.target.value })}
                data-testid="ai-service-requests"
              />
            </label>
            <label className="text-xs md:col-span-2">
              <span className="opacity-70">Preferred memorial-donation charity (optional)</span>
              <input
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                placeholder="e.g. Local hospice, cancer research, an orphanage they supported"
                value={prefs.charity_for_donations}
                onChange={(e) => setPrefs({ ...prefs, charity_for_donations: e.target.value })}
                data-testid="ai-service-charity"
              />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => setOpen(false)} className="text-xs opacity-70 hover:opacity-100" data-testid="ai-service-cancel">Cancel</button>
            <button
              type="button"
              onClick={run}
              disabled={busy}
              className="text-xs rounded-full px-3 py-1.5 font-medium inline-flex items-center gap-1.5"
              style={{ background: "#B25D38", color: "white", opacity: busy ? 0.6 : 1 }}
              data-testid="ai-service-generate"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {busy ? "Drafting…" : "Generate ceremony order"}
            </button>
          </div>
          <div className="text-[10px] opacity-60 italic">
            AI drafts are a starting point — always review and adapt to your family's traditions and wishes.
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * ServiceOrderShareRow — appears once the AI has produced a ceremony
 * order. Three one-click share targets (WhatsApp, Email, Copy) plus a
 * details toggle. Everything is client-side — the ceremony text is
 * already in memory so no extra API round-trip is needed.
 */
export function ServiceOrderShareRow({ result, personName }) {
  const [copied, setCopied] = useState(false);
  const text = formatServiceOrderText(result, personName);
  const subject = personName ? `Order of service for ${personName}` : "Order of service";
  const links = buildShareLinks(text, subject);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard is unavailable in this browser.");
    }
  };

  return (
    <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2" style={{ borderColor: "#B25D3822" }} data-testid="ai-service-share-row">
      <div className="text-xs opacity-70 mr-1">Share this draft with family:</div>
      <a
        href={links.whatsapp}
        target="_blank"
        rel="noreferrer"
        className="text-xs rounded-full px-3 py-1.5 font-medium inline-flex items-center gap-1.5"
        style={{ background: "#25D366", color: "white" }}
        data-testid="ai-service-share-whatsapp"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
        WhatsApp
      </a>
      <a
        href={links.email}
        className="text-xs rounded-full px-3 py-1.5 font-medium inline-flex items-center gap-1.5 border"
        style={{ borderColor: "#B25D38", color: "#B25D38" }}
        data-testid="ai-service-share-email"
      >
        ✉ Email
      </a>
      <button
        type="button"
        onClick={copy}
        className="text-xs rounded-full px-3 py-1.5 font-medium inline-flex items-center gap-1.5 border"
        style={{ borderColor: "#B25D38", color: "#B25D38" }}
        data-testid="ai-service-share-copy"
      >
        {copied ? <><Check className="w-3 h-3" /> Copied</> : <>📋 Copy text</>}
      </button>
    </div>
  );
}
